import { useCallback } from 'react';
import _, { isEmpty } from 'lodash';

import { makeStyles } from '@material-ui/core/styles';
import DataTable from '../../data-table/DataTable';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { useQueryLoader } from '../../data-table/Loaders';
import {
    COLLECTOR_LIST_COLUMNS,
    COLLECTOR_LIST_DATA_SOURCE,
    COLLECTOR_MAX_ELEMENTS_DISTRIBUTED,
    COLLECTOR_MAX_ELEMENTS_EMBEDDED,
} from './CollectorsAdminConstants';

const initialSort = [{ property: 'name', direction: SORT_ASC }];

const useStyles = makeStyles((theme) => ({
    tabTable: {
        paddingTop: theme.spacing(1),

        '@media print': {
            paddingTop: 0,
        },
    },
}));

export function monitoredElementsRenderer(row) {
    const monitoredCount = row.deviceCount;
    const { embedded } = row;
    const embeddedCount = COLLECTOR_MAX_ELEMENTS_EMBEDDED;
    const distributedCount = COLLECTOR_MAX_ELEMENTS_DISTRIBUTED;
    const overLimit = (embedded && monitoredCount > embeddedCount) || (!embedded && monitoredCount > distributedCount);
    return (
        <span
            style={{
                color: overLimit ? 'red' : '',
            }}
        >
            {monitoredCount} / {embedded ? embeddedCount : distributedCount}
        </span>
    );
}

export default function CollectorsAdminCollectorListPanel({
    setActionsDisabled,
    setTableSelection,
    setTableState,
    triggerRef,
    setCollectorCanDelete,
    setSelectedCollectors,
}) {
    const classes = useStyles();
    const loader = useQueryLoader(COLLECTOR_LIST_DATA_SOURCE, { disableFilterState: true });

    /**
     * @private
     * This method checks to see whether or not the delete button should be enabled for our list.
     * Delete will not be enabled in the following circumstances:
     *
     * - All of the selectors are picked
     * - Nothing is picked
     * - The embedded collector is picked
     */
    const checkCanDelete = useCallback(
        (tableSelection, tableRows) => {
            const collectorIds = tableSelection.selection;
            const tableCollection = Object.values(tableRows).map((item) => item.row);

            const embeddedSelected =
                _(collectorIds)
                    .map((id) => tableCollection[id])
                    .filter({ embedded: true })
                    .value().length > 0;

            setCollectorCanDelete(
                collectorIds.length < tableCollection.length && !embeddedSelected && collectorIds.length > 0
            );
            setSelectedCollectors(tableCollection.filter((item) => tableSelection.selection.includes(item._id)));
        },
        [setCollectorCanDelete, setSelectedCollectors]
    );

    const handleTableSelectionChange = useCallback(
        (currentTableSelection, tableRows) => {
            const disableActions =
                !currentTableSelection ||
                (isEmpty(currentTableSelection?.selection) && !currentTableSelection.inverted);

            setTableSelection(currentTableSelection);
            setActionsDisabled(disableActions);

            checkCanDelete(currentTableSelection, tableRows);
        },
        [setTableSelection, setActionsDisabled, checkCanDelete]
    );

    return (
        <DataTable
            className={classes.tabTable}
            tableId="admin-collectors-list"
            loader={loader}
            columns={COLLECTOR_LIST_COLUMNS}
            triggerRef={triggerRef}
            visibleRows={0}
            showSelectionColumn
            sorters={initialSort}
            onStateChanged={setTableState}
            selectionOptions={{
                mode: SELECTIONMODE_MULTI,
                allowSelectAll: true,
                idMapping: '_id',
                onChange: handleTableSelectionChange,
            }}
            disableColumnRemover
        />
    );
}
